import React, { useState, useCallback, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Close, HelpOutlined } from '@mui/icons-material'
import axios from 'src/utilities/http-client'

interface VocabularyData {
  phrase: string
  soundsLike: string
  ipa: string
  displayAs: string
}

interface AddVocabularyModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  initialWord?: string
}

export function AddVocabularyModal({
  open,
  onClose,
  onSuccess,
  initialWord = '',
}: AddVocabularyModalProps) {
  let [vocabularyData, setVocabularyData] = useState<VocabularyData>({
    phrase: '',
    soundsLike: '',
    ipa: '',
    displayAs: '',
  })
  let [isSubmitting, setIsSubmitting] = useState(false)
  let [error, setError] = useState<string | null>(null)

  // Initialize with initial word when modal opens
  useEffect(() => {
    if (open) {
      setVocabularyData({
        phrase: initialWord,
        soundsLike: '',
        ipa: '',
        displayAs: '',
      })
      setError(null)
    }
  }, [open, initialWord])

  let handleInputChange = useCallback(
    (field: keyof VocabularyData) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setVocabularyData((prev) => ({
          ...prev,
          [field]: event.target.value,
        }))
      },
    [],
  )

  let handleSubmit = useCallback(async () => {
    if (!vocabularyData.phrase.trim()) {
      setError('Phrase is required')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await axios.post('/api/create/video/vocabulary', {
        phrase: vocabularyData.phrase.trim(),
        soundsLike: vocabularyData.soundsLike.trim() || undefined,
        ipa: vocabularyData.ipa.trim() || undefined,
        displayAs: vocabularyData.displayAs.trim() || undefined,
      })

      onSuccess()
      onClose()
    } catch (e) {
      let errorMessage =
        e instanceof Error ? e.message : 'Failed to add vocabulary term'
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }, [vocabularyData, onSuccess, onClose])

  let handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit],
  )

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Add Vocabulary Term</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add custom vocabulary to improve transcription accuracy for technical,
          medical, or industry-specific terms.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Phrase"
            placeholder="e.g., epinephrine, bradycardia"
            value={vocabularyData.phrase}
            onChange={handleInputChange('phrase')}
            onKeyPress={handleKeyPress}
            required
            helperText="The word or phrase as it should be recognized"
            disabled={isSubmitting}
          />

          <TextField
            fullWidth
            label="Sounds Like"
            placeholder="e.g., ep-i-nef-rin"
            value={vocabularyData.soundsLike}
            onChange={handleInputChange('soundsLike')}
            onKeyPress={handleKeyPress}
            helperText="Phonetic spelling to help recognition (optional)"
            disabled={isSubmitting}
          />

          <TextField
            fullWidth
            label="IPA Pronunciation"
            placeholder="e.g., /ˌɛpɪˈnɛfrɪn/"
            value={vocabularyData.ipa}
            onChange={handleInputChange('ipa')}
            onKeyPress={handleKeyPress}
            helperText="International Phonetic Alphabet notation (optional)"
            disabled={isSubmitting}
            slotProps={{
              input: {
                style: { fontFamily: 'monospace' },
              },
            }}
          />

          <TextField
            fullWidth
            label="Display As"
            placeholder="e.g., Epinephrine"
            value={vocabularyData.displayAs}
            onChange={handleInputChange('displayAs')}
            onKeyPress={handleKeyPress}
            helperText="How the word should appear in transcripts (optional)"
            disabled={isSubmitting}
          />

          <Alert severity="info" icon={<HelpOutlined />}>
            <Typography variant="body2">
              <strong>Tips:</strong> Only the phrase is required. Additional
              fields help improve recognition accuracy:
              <br />• <strong>Sounds Like:</strong> Helps with pronunciation
              variants
              <br />• <strong>IPA:</strong> Precise pronunciation guide
              <br />• <strong>Display As:</strong> Preferred formatting in
              transcripts
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !vocabularyData.phrase.trim()}
        >
          {isSubmitting ? <CircularProgress size={20} /> : 'Add Term'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
